import _, {isUndefined} from 'lodash';
import React, {FC, useMemo, useState} from 'react';

import {ModifyCardGroupsDropdownTypesEnum} from '../../../../../../../../../../src/components/common/dropdowns/cardGroups/modifyCardGroupDropdown';
import {isAutoGlobalCard} from '../../../../../../../../../../src/helpers/models/cardHelpers';
import {isTeammateNamespace} from '../../../../../../../../../../src/helpers/models/namespaceHelpers';
import {CardGroupModel} from '../../../../../../../../../../src/models/api/cardGroupModel';
import {CardModel} from '../../../../../../../../../../src/models/api/cardModel';
import {ApiSendOptions} from '../../../../../../../../../../src/services/http/http';
import {ContactsManagerPanelId} from '../../../../../../../../../../src/store/panels/panelStateBase';
import {Trans} from '../../../../../../../../../shared/helpers/src/internationalization/react';
import {AsyncHandlerOf, HandlerOf} from '../../../../../../../../../shared/helpers/src/react/reactHelpers';
import {
  ButtonChrome,
  ButtonInheritableProps,
} from '../../../../../../../../../shared/ui/src/components/buttons/buttonChrome';
import {ButtonVariantsEnum} from '../../../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {ButtonContent} from '../../../../../../../../../shared/ui/src/components/buttons/buttonContent';
import {ChromelessButton} from '../../../../../../../../../shared/ui/src/components/buttons/chromelessButton';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../../../../shared/ui/src/components/popovers/popoverCoordinator';
import {useContactsManagerContext} from '../../../../contactsManagerContext';
import {OtherCardNamespaceConfirmationDialogContainer} from '../../../../dialogs/otherCardNamespaceConfirmationDialogContainer';
import {
  buildContactsManagerCreateCardGroupEvent,
  buildContactsManagerEditCardGroupsEvent,
  ContactsManagerEditCardGroupsTrackingActionsEnum,
} from '../../../../helpers/trackingHelpers';
import {ContactsTableMultiListsCardsDropdown} from './contactsTableMultiListsCardsDropdown';
import {ContactsTableMultiListsChevronAction} from './contactsTableMultiListsChevronAction';

/*
 * Props.
 */

interface ContactsTableMultiListsActionProps {
  cards: ReadonlyArray<CardModel>;
  namespace: string;
  shouldHideRemoveFromListsAction?: boolean;
  showContactsManagerContactsAddedToListSuccessBanner: HandlerOf<ContactsManagerPanelId>;
  showContactsManagerContactsRemovedFromListSuccessBanner: HandlerOf<ContactsManagerPanelId>;
  addCardsToCardGroupsAsync: (
    groupIds: ReadonlyArray<number>,
    cardIds: ReadonlyArray<number>,
    options?: ApiSendOptions,
  ) => Promise<void>;
  removeCardsFromCardGroupsAsync: (
    groupIds: ReadonlyArray<number>,
    cardIds: ReadonlyArray<number>,
    options?: ApiSendOptions,
  ) => Promise<void>;
}

interface RenderCardGroupsDropdownOptions {
  namespace: string;
  type: ModifyCardGroupsDropdownTypesEnum;
  popoverProps?: PopoverTransferProps;
  alreadySelectedGroupIds?: ReadonlyArray<number>;
  allCardsGroupIds?: ReadonlyArray<number>;
  onAddGroupsClick: AsyncHandlerOf<ReadonlyArray<CardGroupModel>>;
  onRemoveGroupsClick: AsyncHandlerOf<ReadonlyArray<CardGroupModel>>;
  onGroupCreated: HandlerOf<CardGroupModel>;
}

/*
 * Component.
 */

export const ContactsTableMultiListsAction: FC<ContactsTableMultiListsActionProps> = (props) => {
  const {
    cards,
    namespace: passedInNamespace,
    shouldHideRemoveFromListsAction,
    showContactsManagerContactsAddedToListSuccessBanner,
    showContactsManagerContactsRemovedFromListSuccessBanner,
    addCardsToCardGroupsAsync,
    removeCardsFromCardGroupsAsync,
  } = props;
  const namespace = useMemo(() => buildNamespaceToUse(passedInNamespace, cards), [passedInNamespace, cards]);
  const allCardsGroupIds = useMemo(
    () =>
      _(cards.map((c) => c.groupIds))
        .flatten()
        .uniq()
        .value(),
    [cards],
  );
  const alreadySelectedGroupIds = useMemo(() => buildAlreadySelectedGroupIds(cards), [cards]);
  const isAutoGlobalCards = useMemo(() => cards.every(isAutoGlobalCard), [cards]);

  const [isUpdatingCardGroups, setIsUpdatingCardGroups] = useState(false);
  const [isShowingConfirmNamespaceDialog, setIsShowingConfirmNamespaceDialog] = useState(false);
  const [selectedCardGroups, setSelectedCardGroups] = useState<ReadonlyArray<CardGroupModel>>();
  const {panelId, onTrackHandler} = useContactsManagerContext();

  const onAddGroupsClick = async (groups: ReadonlyArray<CardGroupModel>) => {
    if (isAutoGlobalCards) {
      setIsShowingConfirmNamespaceDialog(true);
      setSelectedCardGroups(groups);
      return;
    }

    setIsUpdatingCardGroups(true);
    try {
      await addCardsToCardGroupsAsync(
        groups.map((group) => group.id),
        cards.map((card) => card.id),
      );
      showContactsManagerContactsAddedToListSuccessBanner(panelId);
      onTrackHandler(
        buildContactsManagerEditCardGroupsEvent(
          ContactsManagerEditCardGroupsTrackingActionsEnum.ADD,
          cards.length,
        ),
      );
    } catch (err) {
      // TODO: Show fail banner.
    }
    setIsUpdatingCardGroups(false);
  };

  const onRemoveGroupsClick = async (groups: ReadonlyArray<CardGroupModel>) => {
    setIsUpdatingCardGroups(true);
    try {
      await removeCardsFromCardGroupsAsync(
        groups.map((group) => group.id),
        cards.map((card) => card.id),
      );
      showContactsManagerContactsRemovedFromListSuccessBanner(panelId);
      onTrackHandler(
        buildContactsManagerEditCardGroupsEvent(
          ContactsManagerEditCardGroupsTrackingActionsEnum.REMOVE,
          cards.length,
        ),
      );
    } catch (err) {
      // TODO: Show fail banner.
    }
    setIsUpdatingCardGroups(false);
  };

  const onGroupCreated = (group: CardGroupModel) => {
    onTrackHandler(buildContactsManagerCreateCardGroupEvent(group));
  };

  const maybeRenderNamespaceConfirmationDialog = () => {
    if (
      !isShowingConfirmNamespaceDialog ||
      isUndefined(selectedCardGroups) ||
      selectedCardGroups.length === 0
    ) {
      return null;
    }

    return (
      <OtherCardNamespaceConfirmationDialogContainer
        cardGroups={selectedCardGroups}
        cards={cards}
        panelId={panelId}
        onClose={() => setIsShowingConfirmNamespaceDialog(false)}
      />
    );
  };

  return (
    <>
      <ButtonChrome variant={ButtonVariantsEnum.SECONDARY} isDisabled={isUpdatingCardGroups}>
        {(buttonProps) => (
          <>
            {renderAddToListsButton(buttonProps, {
              alreadySelectedGroupIds,
              allCardsGroupIds,
              namespace,
              type: ModifyCardGroupsDropdownTypesEnum.ADD,
              onAddGroupsClick,
              onRemoveGroupsClick,
              onGroupCreated,
            })}
            {!shouldHideRemoveFromListsAction && (
              <ContactsTableMultiListsChevronAction
                buttonProps={buttonProps}
                renderCardGroupDropdown={(options) =>
                  renderCardGroupsDropdown({
                    popoverProps: options.popoverProps,
                    type: options.type,
                    alreadySelectedGroupIds,
                    allCardsGroupIds,
                    namespace,
                    onAddGroupsClick,
                    onRemoveGroupsClick,
                    onGroupCreated,
                  })
                }
              />
            )}
          </>
        )}
      </ButtonChrome>
      {maybeRenderNamespaceConfirmationDialog()}
    </>
  );
};

/*
 * Helpers.
 */

function renderAddToListsButton(
  buttonProps: ButtonInheritableProps,
  options: RenderCardGroupsDropdownOptions,
) {
  return (
    <PopoverCoordinator
      renderButton={(isOpen) => (
        <ChromelessButton hasHover={isOpen} isDisabled={buttonProps.isDisabled}>
          <ButtonContent
            size={buttonProps.size}
            variant={buttonProps.variant}
            color={buttonProps.color}
            isRounded={buttonProps.isRounded}
            isDisabled={buttonProps.isDisabled}
          >
            <Trans id="contact-manager-table-multi-select-actions-add-to-lists">Add to lists</Trans>
          </ButtonContent>
        </ChromelessButton>
      )}
      renderModal={(popoverProps) => renderCardGroupsDropdown({...options, popoverProps})}
    />
  );
}

function renderCardGroupsDropdown(options: RenderCardGroupsDropdownOptions) {
  const {
    popoverProps,
    namespace,
    type,
    alreadySelectedGroupIds,
    allCardsGroupIds,
    onAddGroupsClick,
    onRemoveGroupsClick,
    onGroupCreated,
  } = options;

  if (!popoverProps) {
    return null;
  }

  return (
    <ContactsTableMultiListsCardsDropdown
      popoverProps={popoverProps}
      namespace={namespace}
      type={type}
      alreadySelectedGroupIds={alreadySelectedGroupIds}
      allCardsGroupIds={allCardsGroupIds}
      onAddGroupsClick={onAddGroupsClick}
      onRemoveGroupsClick={onRemoveGroupsClick}
      onGroupCreated={onGroupCreated}
    />
  );
}

function buildAlreadySelectedGroupIds(cards: ReadonlyArray<CardModel>) {
  return _(cards)
    .map((card) => card.groupIds)
    .flatten()
    .countBy()
    .pickBy((count) => count === cards.length)
    .keys()
    .compact()
    .map((groupId) => Number(groupId))
    .value();
}

function buildNamespaceToUse(namespace: string, cards: ReadonlyArray<CardModel>) {
  const teammateCard = cards.find((card) => isTeammateNamespace(card.namespace));
  return teammateCard?.namespace ?? namespace;
}
